import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function ContactPage() {
  // SEO Configuration
  useSEO({
    title: 'Contact Painterrr | Get in Touch with South Africa\'s Premier Painting Network',
    description: 'Contact Painterrr for any questions about our verified painting professionals in Cape Town and across South Africa. We\'re here to help you find the perfect painter for your project.',
    keywords: 'contact painterrr, painting services contact, cape town painters contact, south africa painting network',
    schema: generateWebPageSchema({
      name: 'Contact Painterrr',
      description: 'Contact Painterrr for any questions about our verified painting professionals in Cape Town and across South Africa.',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`
    })
  });

  useEffect(() => {
    // Load Elfsight script
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-[#e9f638] rounded-lg flex items-center justify-center">
                <i className="ri-paint-brush-fill text-2xl text-black"></i>
              </div>
              <span className="text-2xl font-bold text-black">Painterrr</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-gray-700 hover:text-[#e9f638] transition-colors whitespace-nowrap">
                Home
              </Link>
              <Link to="/listing" className="text-gray-700 hover:text-[#e9f638] transition-colors whitespace-nowrap">
                Browse Painters
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-[#e9f638] transition-colors whitespace-nowrap">
                About
              </Link>
              <Link to="/contact" className="text-black font-medium whitespace-nowrap">
                Contact
              </Link>
              <Link
                to="/get-a-quote"
                className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-[#d4e030] transition-colors whitespace-nowrap"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-black mb-6">
            Get in <strong className="text-[#e9f638]">Touch</strong> with Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Have questions about our services? Need help finding the perfect painter? We're here to assist you every step of the way.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Email */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-mail-line text-3xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-center mb-2">Email Us</h3>
              <p className="text-gray-600 mb-3">Send us a message anytime</p>
              <p className="text-black hover:text-[#e9f638] font-medium text-center">
                Use the contact form below
              </p>
            </div>

            {/* Phone */}
            <div className="text-center p-8 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-3xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Call Us</h3>
              <p className="text-gray-600 mb-3">Mon-Fri from 8am to 5pm</p>
              <a href="tel:+27123456789" className="text-black hover:text-[#e9f638] font-medium">
                +27 12 345 6789
              </a>
            </div>

            {/* Location */}
            <div className="text-center p-8 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Visit Us</h3>
              <p className="text-gray-600 mb-3">Cape Town, South Africa</p>
              <p className="text-black font-medium">Serving all of South Africa</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-black mb-4">
                Send Us a <strong className="text-[#e9f638]">Message</strong>
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll get back to you as soon as possible
              </p>
            </div>

            {/* Elfsight Form */}
            <div className="bg-black rounded-2xl shadow-lg p-8">
              <div className="elfsight-app-b2582d3f-f230-4eb0-aabc-eec0745fba11" data-elfsight-app-lazy></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Frequently Asked <strong className="text-[#e9f638]">Questions</strong>
            </h2>
            <p className="text-lg text-gray-600">Quick answers to common questions</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-gray-50 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-black mb-3">
                <i className="ri-question-line text-[#e9f638] mr-2"></i>
                How quickly can I get a quote?
              </h3>
              <p className="text-gray-600 pl-8">
                Most painters respond within 24 hours. You can request quotes from multiple painters at once to compare options quickly.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-black mb-3">
                <i className="ri-question-line text-[#e9f638] mr-2"></i>
                Are all painters verified?
              </h3>
              <p className="text-gray-600 pl-8">
                Yes! Every painter on Painterrr is thoroughly vetted, including background checks, license verification, and customer reviews.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-black mb-3">
                <i className="ri-question-line text-[#e9f638] mr-2"></i>
                Is there a fee to use Painterrr?
              </h3>
              <p className="text-gray-600 pl-8">
                No! Painterrr is completely free for homeowners and businesses. You only pay the painter directly for their services.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-black mb-3">
                <i className="ri-question-line text-[#e9f638] mr-2"></i>
                What areas do you cover?
              </h3>
              <p className="text-gray-600 pl-8">
                We have verified painters across Cape Town and throughout South Africa, including all major cities and surrounding areas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#e9f638]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Start Your <strong>Painting Project</strong>?
          </h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Get free quotes from verified professional painters in your area today
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/get-a-quote"
              className="bg-black text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap"
            >
              Get Free Quotes
            </Link>
            <Link
              to="/listing"
              className="bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
            >
              Browse Painters
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400 text-sm">
                Connecting you with South Africa's most trusted professional painters.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-[#e9f638] transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/listing" className="text-gray-400 hover:text-[#e9f638] transition-colors">
                    Browse Painters
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-[#e9f638] transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-[#e9f638] transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Services</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-gray-400">Interior Painting</li>
                <li className="text-gray-400">Exterior Painting</li>
                <li className="text-gray-400">Commercial Painting</li>
                <li className="text-gray-400">Residential Painting</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <span className="text-gray-400">
                    Use the contact form above
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
